import { useEffect, useState } from 'react';
import { useFocusTrap } from '../components/useFocusTrap';

const NAME_MIN = 2;
const NAME_MAX = 120;
const INSTRUCTIONS_MAX = 2000;

function Sheet({ label, onClose, busy, onSubmit, children }) {
  const sheetRef = useFocusTrap(true);
  return (
    <div className="gym-period-sheet-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
      <form
        ref={sheetRef}
        tabIndex={-1}
        className="gym-form gym-period-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        noValidate
        onSubmit={onSubmit}
        onKeyDown={(event) => { if (event.key === 'Escape' && !busy) { event.preventDefault(); onClose(); } }}
      >
        {children}
      </form>
    </div>
  );
}

/**
 * Alta y edición de un ejercicio propio del gimnasio. Zona, músculo y equipo se eligen de las
 * listas del catálogo (`GET /exercises/options`) para que filtros y alternativas sigan
 * funcionando; el nombre y las instrucciones son texto libre.
 */
export function ExerciseFormSheet({ api, exercise, onSaved, onClose }) {
  const editing = Boolean(exercise);
  const [options, setOptions] = useState({ status: 'loading', bodyParts: [], targets: [], equipment: [] });
  const [form, setForm] = useState({
    name: (exercise && exercise.name) || '',
    bodyPart: (exercise && exercise.bodyPart) || '',
    target: (exercise && exercise.target) || '',
    equipment: (exercise && exercise.equipment) || '',
    instructions: (exercise && exercise.instructions) || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    api.getExerciseOptions()
      .then((result) => {
        if (active) setOptions({ status: 'ready', bodyParts: result.bodyParts || [], targets: result.targets || [], equipment: result.equipment || [] });
      })
      .catch((caught) => {
        if (active) setOptions({ status: 'error', bodyParts: [], targets: [], equipment: [], message: caught.message || 'No se pudieron cargar las listas.' });
      });
    return () => { active = false; };
  }, [api]);

  const setField = (name) => (event) => setForm((current) => ({ ...current, [name]: event.target.value }));

  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    const name = form.name.trim();
    if (name.length < NAME_MIN || name.length > NAME_MAX) { setError(`El nombre debe tener entre ${NAME_MIN} y ${NAME_MAX} caracteres.`); return; }
    if (!form.bodyPart || !form.target || !form.equipment) { setError('Elegí la zona del cuerpo, el músculo principal y el equipo.'); return; }
    const payload = { name, bodyPart: form.bodyPart, target: form.target, equipment: form.equipment, instructions: form.instructions.trim() };
    setBusy(true);
    setError('');
    try {
      const saved = editing ? await api.updateExercise(exercise.id, payload) : await api.createExercise(payload);
      onSaved(saved, editing);
    } catch (caught) {
      setError(caught.message || 'No se pudo guardar el ejercicio.');
      setBusy(false);
    }
  }

  const selects = [
    ['bodyPart', 'Zona del cuerpo', options.bodyParts],
    ['target', 'Músculo principal', options.targets],
    ['equipment', 'Equipo', options.equipment],
  ];
  const title = editing ? 'Editar ejercicio' : 'Nuevo ejercicio';

  return (
    <Sheet label={title} onClose={onClose} busy={busy} onSubmit={submit}>
      <h2 className="gym-period-sheet__title">{title}</h2>
      {error && <p role="alert" className="gym-form__error">{error}</p>}
      {options.status === 'error' && <p role="alert" className="gym-form__error">{options.message}</p>}
      <div className="gym-field">
        <label htmlFor="exercise-form-name" className="gym-field__label">Nombre</label>
        <input id="exercise-form-name" type="text" className="gym-input" required maxLength={NAME_MAX} value={form.name} disabled={busy} onChange={setField('name')} />
      </div>
      {selects.map(([key, label, items]) => (
        <div className="gym-field" key={key}>
          <label htmlFor={`exercise-form-${key}`} className="gym-field__label">{label}</label>
          <select id={`exercise-form-${key}`} className="gym-input" required value={form[key]} disabled={busy || options.status !== 'ready'} onChange={setField(key)}>
            <option value="">{options.status === 'loading' ? 'Cargando…' : 'Elegí una opción'}</option>
            {/* El valor actual se conserva aunque todavía no hayan llegado las listas. */}
            {form[key] && !items.some((item) => item.value === form[key]) && <option value={form[key]}>{form[key]}</option>}
            {items.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </div>
      ))}
      <div className="gym-field">
        <label htmlFor="exercise-form-instructions" className="gym-field__label">Instrucciones (opcional)</label>
        <textarea id="exercise-form-instructions" className="gym-input" maxLength={INSTRUCTIONS_MAX} value={form.instructions} disabled={busy} onChange={setField('instructions')} />
      </div>
      <div className="gym-period-sheet__actions">
        <button type="submit" className="gym-button gym-button--primary" disabled={busy}>{busy ? 'Guardando…' : (editing ? 'Guardar cambios' : 'Crear ejercicio')}</button>
        <button type="button" className="gym-button gym-button--secondary" disabled={busy} onClick={onClose}>Cancelar</button>
      </div>
    </Sheet>
  );
}

/** Nombre propio para un ejercicio del catálogo; el original queda visible y se puede recuperar. */
export function ExerciseRenameSheet({ api, exercise, onSaved, onClose }) {
  const [name, setName] = useState(exercise.name || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const originalName = exercise.hasCustomName ? exercise.originalName : exercise.name;

  async function run(action) {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      onSaved(await action());
    } catch (caught) {
      setError(caught.message || 'No se pudo cambiar el nombre.');
      setBusy(false);
    }
  }

  function submit(event) {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < NAME_MIN || trimmed.length > NAME_MAX) { setError(`El nombre debe tener entre ${NAME_MIN} y ${NAME_MAX} caracteres.`); return; }
    run(() => api.setExerciseName(exercise.id, trimmed));
  }

  return (
    <Sheet label="Cambiar nombre" onClose={onClose} busy={busy} onSubmit={submit}>
      <h2 className="gym-period-sheet__title">Cambiar nombre</h2>
      <p className="gym-period-sheet__text">Nombre original: {originalName}</p>
      {error && <p role="alert" className="gym-form__error">{error}</p>}
      <div className="gym-field">
        <label htmlFor="exercise-rename-name" className="gym-field__label">Nombre</label>
        <input id="exercise-rename-name" type="text" className="gym-input" required maxLength={NAME_MAX} value={name} disabled={busy} onChange={(event) => setName(event.target.value)} />
      </div>
      <div className="gym-period-sheet__actions">
        <button type="submit" className="gym-button gym-button--primary" disabled={busy}>{busy ? 'Guardando…' : 'Guardar'}</button>
        {exercise.hasCustomName && (
          <button type="button" className="gym-button gym-button--secondary" disabled={busy} onClick={() => run(() => api.removeExerciseName(exercise.id))}>Volver al nombre original</button>
        )}
        <button type="button" className="gym-button gym-button--secondary" disabled={busy} onClick={onClose}>Cancelar</button>
      </div>
    </Sheet>
  );
}
